// Market Intelligence REST client.
//
// Plain fetch() wrapper — the MI endpoints are new and don't fit the
// legacy ServiceFactory pipeline. Each call returns the parsed JSON body
// or throws.
//
// Tenant is passed as `x-account-id` per the MI spec, and also as
// `x-app-id` for parity with the rest of the app in case the backend
// prefers that header instead.

import endpoints from './index';
import account from '../data/account';
import { AuthBase } from '../../plunk/service/auth-base';
import { refreshTokens as cognitoRefresh } from '../auth/cognito';
import Auth from '../functions/authenticate';
import __JSONbigFactory from 'json-bigint';

// Parse server JSON with BigInt-safe ID handling. Plunk's backend returns
// long IDs (e.g. location.id = 1876489731429114200) as raw JSON numbers,
// which exceed Number.MAX_SAFE_INTEGER and lose precision via native
// JSON.parse. storeAsString keeps them as strings so they round-trip
// intact through dropdowns / API calls.
const JSONbigString = __JSONbigFactory({
  storeAsString: true
});
const root = endpoints.marketIntelligence.root;
const agentRoot = endpoints.agent.root;

// Proactively refresh the ID token if it's expired or near expiry. The
// MI Lambda's downstream returns 500 (not 401) for expired tokens, so
// our retry-on-5xx loop can't tell that the token is the problem.
// Verify locally before sending. Buffer of 30s avoids race conditions
// where the token expires mid-request.
const TOKEN_REFRESH_BUFFER_SEC = 30;
let _refreshInFlight = null;
function tokenExp(idToken) {
  if (!idToken) return null;
  try {
    const payload = idToken.split('.')[1];
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return claims.exp || null;
  } catch (e) {
    return null;
  }
}

// Exported for other new-rail clients (Plunk Assistant panel) — single-
// flight proactive refresh, returns the freshest ID token available.
export async function ensureFreshToken() {
  const idToken = AuthBase.idToken;
  if (!idToken) return null;
  const exp = tokenExp(idToken);
  const now = Math.floor(Date.now() / 1000);
  if (!exp || now < exp - TOKEN_REFRESH_BUFFER_SEC) {
    // Token still valid for at least the buffer window.
    return idToken;
  }

  // Token expired or near-expiry. Refresh once; multiple concurrent
  // callers share the same in-flight promise.
  if (!_refreshInFlight) {
    const refreshToken = AuthBase.refreshToken;
    if (!refreshToken) {
      // SSO mode keeps the refresh token SERVER-SIDE in the broker —
      // refresh by re-reading the broker session (it mints fresh
      // tokens). Without this, a >60min tab sends a stale ID token and
      // the MI Lambda answers 500 (not 401) — the "Could not load
      // locations: 500" report (2026-07-14). Non-SSO with no refresh
      // token falls through to the stale token as before.
      _refreshInFlight = Auth.refreshSsoTokens().then(fresh => fresh || idToken).catch(() => idToken).finally(() => {
        _refreshInFlight = null;
      });
      return _refreshInFlight;
    }
    _refreshInFlight = cognitoRefresh(refreshToken).then(tokens => {
      AuthBase.tokens = tokens;
      return tokens.idToken;
    }).catch(err => {
      console.warn('[MI] proactive refresh failed', err && err.message);
      // Fall through — the request will get 401 and trigger
      // the AuthBase failure path (sign-out + redirect).
      return idToken;
    }).finally(() => {
      _refreshInFlight = null;
    });
  }
  return _refreshInFlight;
}
export async function headers() {
  const {
    tenantId
  } = account.get();
  const h = {
    'Content-Type': 'application/json',
    'x-account-id': tenantId,
    'x-app-id': tenantId
  };
  // Per Cognito spec: use IdToken on calls into the Plunk API.
  const idToken = await ensureFreshToken();
  if (idToken) h['Authorization'] = 'Bearer ' + idToken;
  return h;
}
export function qs(params) {
  const clean = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return clean.length ? `?${clean.join('&')}` : '';
}
const sleep = ms => new Promise(r => setTimeout(r, ms));

// Debug helper — logs auth state when an MI request fails with 5xx. Used
// to distinguish stale/expired token issues from real backend flakes. Logs
// only the token prefix (first 30 chars), never the full JWT. Decoded
// claims include `exp`/`iat` so we can see if the token expired before
// the request fired (which would explain Lambda HttpRequestException).
function logAuthDiag(label, ctx) {
  try {
    const raw = localStorage.getItem('plunkOAuthTokens') || '{}';
    const t = JSON.parse(raw);
    const claims = t.idToken ? JSON.parse(atob(t.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) : null;
    const nowSec = Math.floor(Date.now() / 1000);
    console.warn('[MI auth-diag]', label, {
      ...ctx,
      hasIdToken: Boolean(t.idToken),
      hasRefreshToken: Boolean(t.refreshToken),
      idTokenPrefix: (t.idToken || '').slice(0, 30),
      exp: claims && claims.exp ? new Date(claims.exp * 1000).toISOString() : null,
      iat: claims && claims.iat ? new Date(claims.iat * 1000).toISOString() : null,
      expiredAtRequest: claims && claims.exp ? nowSec > claims.exp : null,
      secondsUntilExp: claims && claims.exp ? claims.exp - nowSec : null,
      email: claims && claims.email,
      tenant: claims && (claims['custom:account'] || claims.tenant_id),
      now: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[MI auth-diag] (failed to decode)', label, ctx, e);
  }
}

// Retry policy for the MI Lambda's known cold-start instability. We
// retry 5xx and network failures, never 4xx (auth/tenant issues won't
// improve on retry). Total worst case: ~13s (1+3+9 backoff) before
// surfacing the error.
const RETRY_DELAYS_MS = [1000, 3000, 9000];
export async function fetchWithRetry(url, options) {
  let lastErr;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const res = await fetch(url, options);
      // Only retry on 5xx. 4xx surfaces immediately.
      if (res.status >= 500 && res.status < 600 && attempt < RETRY_DELAYS_MS.length) {
        logAuthDiag('5xx — retrying', {
          url,
          status: res.status,
          attempt: attempt + 1,
          nextRetryInMs: RETRY_DELAYS_MS[attempt],
          requestId: res.headers.get('x-amzn-requestid'),
          errorType: res.headers.get('errortype')
        });
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      // Final 5xx after retries — log the final state too.
      if (res.status >= 500 && res.status < 600) {
        logAuthDiag('5xx — final (retries exhausted)', {
          url,
          status: res.status,
          requestId: res.headers.get('x-amzn-requestid'),
          errorType: res.headers.get('errortype')
        });
      }
      return res;
    } catch (err) {
      // Network failure / DNS / aborted. Retry if attempts remain.
      lastErr = err;
      if (attempt < RETRY_DELAYS_MS.length) {
        logAuthDiag('network error — retrying', {
          url,
          attempt: attempt + 1,
          error: err && err.message
        });
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      logAuthDiag('network error — final', {
        url,
        error: err && err.message
      });
      throw err;
    }
  }
  if (lastErr) throw lastErr;
}
async function get(path, params) {
  const res = await fetchWithRetry(`${root}${path}${qs(params)}`, {
    method: 'GET',
    headers: await headers()
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  }
  try {
    return JSONbigString.parse(text);
  } catch (e) {
    // Fall back to native parse if BigInt parser chokes (e.g. empty body)
    return text ? JSON.parse(text) : null;
  }
}

// --- Data endpoints ----------------------------------------------------------

export function getLocations() {
  return get('/locations');
}
export function getDashboard({
  location,
  range = 'last_month',
  comparison = 'previous_period'
}) {
  return get('/dashboard', {
    location,
    range,
    comparison
  });
}
export function getKpis({
  location,
  range,
  comparison
}) {
  return get('/dashboard/kpis', {
    location,
    range,
    comparison
  });
}
export function getProducts({
  location,
  range,
  comparison,
  sort = 'momentum',
  top = 20
}) {
  return get('/dashboard/products', {
    location,
    range,
    comparison,
    sort,
    top
  });
}
export function getCampaigns({
  location,
  range,
  comparison
}) {
  return get('/dashboard/campaigns', {
    location,
    range,
    comparison
  });
}
export function getAudiences({
  location,
  range,
  comparison
}) {
  return get('/dashboard/audiences', {
    location,
    range,
    comparison
  });
}
export function getGeo({
  location,
  range,
  comparison,
  level = 'both'
}) {
  return get('/dashboard/geo', {
    location,
    range,
    comparison,
    level
  });
}
export function getDiagnostics({
  location,
  range,
  comparison
}) {
  return get('/diagnostics', {
    location,
    range,
    comparison
  });
}

// --- Agent (AI analysis) -----------------------------------------------------

export function parseBigSafe(text) {
  if (!text) return null;
  try {
    return JSONbigString.parse(text);
  } catch (e) {
    return JSON.parse(text);
  }
}
export async function runAgent({
  agent,
  locationId,
  range,
  comparisonType
}) {
  const res = await fetchWithRetry(`${agentRoot}/run`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify({
      Agent: agent,
      Instruction: `Run ${agent} analysis`,
      Parameters: {
        locationId,
        range,
        comparisonType
      }
    })
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`POST /agent/run failed: ${res.status} ${text}`);
  }
  return parseBigSafe(text);
}

// Latest completed run for one agent + market, tenant-wide (any user). Powers
// the "Last analysis · View" chip without a per-browser cache. 404 → null.
export async function getLatestRun({
  agent,
  locationId
}) {
  if (!agent || !locationId) return null;
  const res = await fetchWithRetry(`${agentRoot}/latest?agent=${encodeURIComponent(agent)}&locationId=${encodeURIComponent(locationId)}`, {
    method: 'GET',
    headers: await headers()
  });
  if (res.status === 404) return null;
  const text = await res.text();
  if (!res.ok) throw new Error(`GET /agent/latest failed: ${res.status} ${text}`);
  return parseBigSafe(text);
}
export async function pollAgentRun(runId) {
  const res = await fetchWithRetry(`${agentRoot}/run/${encodeURIComponent(runId)}`, {
    method: 'GET',
    headers: await headers()
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`GET /agent/run/${runId} failed: ${res.status} ${text}`);
  }
  return parseBigSafe(text);
}
export default {
  getLocations,
  getLatestRun,
  getDashboard,
  getKpis,
  getProducts,
  getCampaigns,
  getAudiences,
  getGeo,
  getDiagnostics,
  runAgent,
  pollAgentRun
};